import { ApiError } from "@/lib/apiClient";

/** What the flow should do after a failed POST. */
export const WriteErrorAction = {
  Poll: "poll",
  RetryPost: "retry-post",
  FailNewId: "fail-new-id",
} as const;

export type WriteErrorActionValue =
  (typeof WriteErrorAction)[keyof typeof WriteErrorAction];

const POLL_CODES = new Set([
  "SEND_UNRESOLVED",
  "UNRESOLVABLE_SUBMISSION",
  "OPERATION_IN_PROGRESS",
  "CONFIRMATION_PENDING_TRANSACTION_HASH",
]);

const RETRY_POST_CODES = new Set([
  "CHAIN_UNREACHABLE",
  "PROVIDER_UNREACHABLE",
]);

/** Extra POSTs after the first failure before falling through to GET poll. */
export const MAX_WRITE_POST_RETRIES = 2;

/** Map a POST failure to poll, retry, or fail (the next attempt mints a new id). */
export function classifyWriteError(error: unknown): WriteErrorActionValue {
  if (!(error instanceof ApiError)) return WriteErrorAction.RetryPost;

  if (typeof error.code === "string" && POLL_CODES.has(error.code)) {
    return WriteErrorAction.Poll;
  }
  if (typeof error.code === "string" && RETRY_POST_CODES.has(error.code)) {
    return WriteErrorAction.RetryPost;
  }
  if (error.code === "REQUEST_ID_CONFLICT") return WriteErrorAction.FailNewId;
  if (error.statusCode >= 500) return WriteErrorAction.RetryPost;
  return WriteErrorAction.FailNewId;
}

export type WriteAttemptResult<TResponse> =
  | { kind: "response"; response: TResponse }
  | { kind: "poll" }
  | { kind: "fail"; error: unknown };

/**
 * Run one confirm-click POST. Retries transient failures, then either returns
 * the payload, tells the flow to poll GET, or fails (optionally keeping the id).
 */
export async function runWriteAttempt<TResponse>(
  post: () => Promise<TResponse>,
): Promise<WriteAttemptResult<TResponse>> {
  let retries = 0;
  while (true) {
    try {
      const response = await post();
      return { kind: "response", response };
    } catch (error) {
      const action = classifyWriteError(error);
      if (action === WriteErrorAction.Poll) return { kind: "poll" };
      if (action === WriteErrorAction.RetryPost && retries < MAX_WRITE_POST_RETRIES) {
        retries += 1;
        continue;
      }
      if (action === WriteErrorAction.RetryPost) return { kind: "poll" };
      return { kind: "fail", error };
    }
  }
}

export const WRITE_ATTEMPT_STORAGE_PREFIX = "myron:write-attempt:";

/** The backend times out SUBMITTED requests well before this. */
export const WRITE_ATTEMPT_TTL_MS = 24 * 60 * 60 * 1000;

export type WriteAttemptScope = {
  ownerId: string;
  flow: string;
  scopeId: string;
};

export type StoredWriteAttempt = {
  requestId: string;
  amount?: number;
  lastUsedAt: number;
};

function storageKey({ ownerId, flow, scopeId }: WriteAttemptScope): string {
  return `${WRITE_ATTEMPT_STORAGE_PREFIX}${ownerId}:${flow}:${scopeId}`;
}

function parseAttempt(raw: string | null): StoredWriteAttempt | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredWriteAttempt;
    if (typeof parsed?.requestId !== "string" || !parsed.requestId) return null;
    if (typeof parsed.lastUsedAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Read a persisted attempt for this scope, or null if missing/invalid. */
export function readWriteAttempt(
  scope: WriteAttemptScope,
): StoredWriteAttempt | null {
  try {
    return parseAttempt(sessionStorage.getItem(storageKey(scope)));
  } catch {
    return null;
  }
}

/** Persist the in-flight request id (and deposit amount, when set). */
export function writeWriteAttempt(
  scope: WriteAttemptScope,
  attempt: StoredWriteAttempt,
): void {
  sessionStorage.setItem(storageKey(scope), JSON.stringify(attempt));
}

/** Drop the persisted attempt after a terminal success, failure, or fail-new-id. */
export function clearWriteAttempt(scope: WriteAttemptScope): void {
  sessionStorage.removeItem(storageKey(scope));
}

/** Drop attempts unused for `WRITE_ATTEMPT_TTL_MS`, plus unreadable entries. */
export function pruneStaleWriteAttempts(now = Date.now()): void {
  try {
    Object.keys(sessionStorage).forEach((key) => {
      if (!key.startsWith(WRITE_ATTEMPT_STORAGE_PREFIX)) return;
      const attempt = parseAttempt(sessionStorage.getItem(key));
      if (!attempt || now - attempt.lastUsedAt > WRITE_ATTEMPT_TTL_MS) {
        sessionStorage.removeItem(key);
      }
    });
  } catch {
    // Storage unavailable.
  }
}

export type RequestIdResolution =
  | { kind: "ready"; requestId: string }
  | { kind: "pending" }
  | { kind: "unknown"; error: unknown };

/**
 * Check the stored attempt before reusing its id: terminal mints a new id,
 * in flight is `pending`, 404 reuses it (if the amount matches), other errors
 * are `unknown`.
 */
export async function resolveRequestId<TStatus>(
  options: WriteAttemptScope & {
    amount?: number;
    fetchStatus: (requestId: string) => Promise<TStatus>;
    isInFlight: (status: TStatus) => boolean;
  },
): Promise<RequestIdResolution> {
  const { amount, fetchStatus, isInFlight, ...scope } = options;
  const stored = readWriteAttempt(scope);
  let requestId: string | undefined;

  if (stored) {
    try {
      const status = await fetchStatus(stored.requestId);
      if (isInFlight(status)) return { kind: "pending" };
      clearWriteAttempt(scope);
    } catch (error) {
      if (!(error instanceof ApiError && error.statusCode === 404)) {
        return { kind: "unknown", error };
      }
      if (amount === undefined || stored.amount === amount) {
        requestId = stored.requestId;
      }
    }
  }

  requestId ??= crypto.randomUUID();
  writeWriteAttempt(scope, {
    requestId,
    ...(amount !== undefined ? { amount } : {}),
    lastUsedAt: Date.now(),
  });
  return { kind: "ready", requestId };
}
